<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

const emit = defineEmits(['open', 'close']);

const props = defineProps({
	placement: {
		type: String,
		default: 'right',
		validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v)
	},
	offset: {
		type: Number,
		default: 8
	},
	openDelayMs: {
		type: Number,
		default: 200
	},
	closeDelayMs: {
		type: Number,
		default: 150
	},
	disabled: {
		type: Boolean,
		default: false
	},
	// Use 'contents' when HoverCard is inside a grid/flex layout and shouldn't affect item sizing
	triggerDisplay: {
		type: String,
		default: 'inline-flex',
		validator: (v) => ['inline-flex', 'contents', 'block', 'flex'].includes(v)
	},
	triggerClass: {
		type: String,
		default: ''
	},
	triggerOpenClass: {
		type: String,
		default: 'bg-primary-color/10'
	}
});

const isOpen = ref(false);
const triggerRef = ref(null);
const contentRef = ref(null);

const position = ref({ top: 0, left: 0 });
const resolvedPlacement = ref(props.placement);

let openTimer = null;
let closeTimer = null;

function clearTimers() {
	if (openTimer) {
		globalThis.clearTimeout(openTimer);
		openTimer = null;
	}
	if (closeTimer) {
		globalThis.clearTimeout(closeTimer);
		closeTimer = null;
	}
}

function scheduleOpen() {
	clearTimers();
	openTimer = globalThis.setTimeout(() => {
		isOpen.value = true;
		nextTick(updatePosition);
	}, props.openDelayMs);
}

function scheduleClose() {
	clearTimers();
	closeTimer = globalThis.setTimeout(() => {
		isOpen.value = false;
	}, props.closeDelayMs);
}

function cancelClose() {
	if (closeTimer) {
		globalThis.clearTimeout(closeTimer);
		closeTimer = null;
	}
}

function updatePosition() {
	if (!triggerRef.value || !contentRef.value) return;

	const triggerEl = triggerRef.value;
	const contentEl = contentRef.value;
	const rect = triggerEl.getBoundingClientRect();
	const contentRect = contentEl.getBoundingClientRect();

	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	const padding = 8;

	function computeForPlacement(p) {
		let top = 0;
		let left = 0;
		switch (p) {
			case 'right':
				top = rect.top + rect.height / 2 - contentRect.height / 2;
				left = rect.right + props.offset;
				break;
			case 'left':
				top = rect.top + rect.height / 2 - contentRect.height / 2;
				left = rect.left - contentRect.width - props.offset;
				break;
			case 'top':
				top = rect.top - contentRect.height - props.offset;
				left = rect.left + rect.width / 2 - contentRect.width / 2;
				break;
			case 'bottom':
				top = rect.bottom + props.offset;
				left = rect.left + rect.width / 2 - contentRect.width / 2;
				break;
		}
		return { top, left };
	}

	function fitsViewport(pos) {
		return pos.left >= padding && pos.top >= padding && pos.left + contentRect.width <= viewportWidth - padding && pos.top + contentRect.height <= viewportHeight - padding;
	}

	function clampToViewport(pos) {
		let { top, left } = pos;
		if (left < padding) left = padding;
		if (left + contentRect.width > viewportWidth - padding) {
			left = viewportWidth - contentRect.width - padding;
		}
		if (top < padding) top = padding;
		if (top + contentRect.height > viewportHeight - padding) {
			top = viewportHeight - contentRect.height - padding;
		}
		return { top, left };
	}

	const opposite = (p) => {
		if (p === 'right') return 'left';
		if (p === 'left') return 'right';
		if (p === 'top') return 'bottom';
		return 'top';
	};

	const preferred = props.placement;
	const candidates = [preferred, opposite(preferred)];
	for (const p of ['right', 'left', 'top', 'bottom']) {
		if (!candidates.includes(p)) candidates.push(p);
	}

	let chosenPlacement = preferred;
	let chosenPos = computeForPlacement(preferred);

	for (const p of candidates) {
		const pos = computeForPlacement(p);
		if (fitsViewport(pos)) {
			chosenPlacement = p;
			chosenPos = pos;
			break;
		}
	}

	// If nothing fits cleanly, clamp the preferred placement (keeps behavior predictable)
	if (!fitsViewport(chosenPos)) {
		chosenPlacement = preferred;
		chosenPos = clampToViewport(chosenPos);
	}

	resolvedPlacement.value = chosenPlacement;
	position.value = clampToViewport(chosenPos);
}

function handleTriggerMouseEnter() {
	if (!props.disabled) {
		scheduleOpen();
	}
}

function handleTriggerMouseLeave() {
	scheduleClose();
}

function handleTriggerFocusIn() {
	if (!props.disabled) {
		clearTimers();
		isOpen.value = true;
		nextTick(updatePosition);
	}
}

function handleTriggerFocusOut(e) {
	// Don't close if focus moved into the content
	if (contentRef.value && contentRef.value.contains(e.relatedTarget)) {
		return;
	}
	scheduleClose();
}

function handleContentMouseEnter() {
	cancelClose();
}

function handleContentPointerDown() {
	// Interactive controls in the teleported card can receive focus just after
	// the trigger loses it. Cancel an in-flight hover close before that happens.
	cancelClose();
}

function handleContentMouseLeave() {
	scheduleClose();
}

function handleContentFocusOut(e) {
	// Don't close if focus moved back to trigger or within content
	if (triggerRef.value && triggerRef.value.contains(e.relatedTarget)) {
		return;
	}
	if (contentRef.value && contentRef.value.contains(e.relatedTarget)) {
		return;
	}
	scheduleClose();
}

function handleKeyDown(e) {
	if (e.key === 'Escape' && isOpen.value) {
		isOpen.value = false;
		triggerRef.value?.focus();
	}
}

function handleScrollOrResize() {
	if (isOpen.value) {
		updatePosition();
	}
}

onMounted(() => {
	document.addEventListener('keydown', handleKeyDown);
	window.addEventListener('scroll', handleScrollOrResize, true);
	window.addEventListener('resize', handleScrollOrResize);
});

onBeforeUnmount(() => {
	clearTimers();
	document.removeEventListener('keydown', handleKeyDown);
	window.removeEventListener('scroll', handleScrollOrResize, true);
	window.removeEventListener('resize', handleScrollOrResize);
});

watch(isOpen, (val) => {
	if (val) {
		emit('open');
		nextTick(updatePosition);
	} else {
		emit('close');
	}
});

watch(
	() => props.placement,
	(newPlacement) => {
		resolvedPlacement.value = newPlacement;
		if (isOpen.value) nextTick(updatePosition);
	}
);

// Close if disabled changes to true while open
watch(
	() => props.disabled,
	(disabled) => {
		if (disabled && isOpen.value) {
			close();
		}
	}
);

// Exposed methods
function close() {
	clearTimers();
	isOpen.value = false;
}

function open() {
	if (!props.disabled) {
		clearTimers();
		isOpen.value = true;
		nextTick(updatePosition);
	}
}

// Expose methods for parent components
defineExpose({
	close,
	open,
	isOpen
});
</script>

<template>
	<span
		ref="triggerRef"
		class="hovercard-trigger inline-flex cursor-pointer outline-none rounded transition-colors duration-150"
		:class="[props.triggerClass, { [props.triggerOpenClass]: isOpen }]"
		:style="{ display: triggerDisplay }"
		tabindex="0"
		role="button"
		aria-haspopup="dialog"
		:aria-expanded="isOpen"
		@mouseenter="handleTriggerMouseEnter"
		@mouseleave="handleTriggerMouseLeave"
		@focusin="handleTriggerFocusIn"
		@focusout="handleTriggerFocusOut"
	>
		<slot name="trigger" />
	</span>

	<Teleport to="body">
		<Transition name="hovercard-fade">
			<div
				v-if="isOpen"
				ref="contentRef"
				class="hovercard-content fixed z-[9999] min-w-[280px] max-w-[340px] p-4 bg-[var(--egs-content-background)] text-color border border-surface rounded-lg shadow-lg pointer-events-auto"
				:class="'hovercard-content--' + resolvedPlacement"
				role="dialog"
				aria-modal="false"
				:style="{ top: position.top + 'px', left: position.left + 'px' }"
				@mouseenter="handleContentMouseEnter"
				@mouseleave="handleContentMouseLeave"
				@pointerdown="handleContentPointerDown"
				@focusin="cancelClose"
				@focusout="handleContentFocusOut"
			>
				<slot name="content" />
			</div>
		</Transition>
	</Teleport>
</template>
